// TODO how to setup signin to client. May differ. But likely Oauth.
// Must be handled directly by the client, not the cache.

import {
  IndividualRefreshEvent,
  IndividualDeleteEvent,
  CollectionSizeUpdateEvent,
  SyncProgressEvent,
  SyncSkippedIdsEvent,
  CacheUpdateEvent,
  CacheDeleteEvent,
  CacheSyncCompleteEvent,
  CacheHydrationCompleteEvent,
  PersistentStateMessageEvent,
} from '@/rec-cache/rec-cache-events'

import { Namespaces } from '@/rec-cache/rec-cache-namespaces'

import { IndexSelector } from '@/rec-cache/rec-cache-index-selector'

import { GeoCoordSelector } from './rec-cache-geo-coord-selector'

import { TypeSelector } from './rec-cache-type-selector'

import { TreeSelector } from './rec-cache-tree-selector'

import { AssetPathSelector } from './rec-cache-asset-path-selector'

import { Individuals } from './rec-cache-named-individuals'

import { ObjectProperty, DataProperty, Iri } from './rec-cache-types'

import { Edge } from './rec-cache-edge'
import { RecComponent } from './rec-cache-component'

import { Traversal } from './rec-cache-gremlin'

import { RecIndividual } from '@/rec-cache/rec-cache-individual'

// TODO rename class...
import { Owl } from '@/rec-cache/owl'

import { RecCacheIDB } from './rec-cache-idb'

import { Literal, LanguageString, JsonLiteral } from './rec-cache-types'
import { GeoCoord, GeoCoords } from './rec-cache-geo'

/// TODO the aggregators / component implementation only works with outgoing relationships.
class RecCache {
  constructor({
    namespaces = {},
    upAggregators = [],
    downAggregators = [],
    client = null,
    geoCoordinateFn = null,
    upTreeEdges = [],
    downTreeEdges = [],
    assetPathTypeCategories = null,
  } = {}) {
    console.log(
      'rec-cache.rec-cache.constructor',
      namespaces,
      client,
      geoCoordinateFn,
      'tree edges:',
      upTreeEdges.length,
      downTreeEdges.length
    )
    this.namespaces = new Namespaces(namespaces)
    this.downAggregators = downAggregators
    this.upAggregators = upAggregators
    this.client = client

    this.geoCoordinatesFn = geoCoordinateFn
    this.upTreeEdges = upTreeEdges
    this.downTreeEdges = downTreeEdges

    // Factory for REC individuals
    this.owl = new Owl(this.namespaces)

    // Main databases for the cache.
    this.individuals = []
    // Id-to-individual lookup index for O(1) access. Maintained alongside
    // `this.individuals` (the reactive array consumers iterate over).
    // Hot path: `refresh()` checks the index instead of running
    // `this.individuals.find(...)` which was ~17ms/individual at 4k+
    // cache size and dominated sync wall-clock.
    this._individualsById = new Map()
    this.edges = []
    // Edge indices for O(1) lookup by source or target value.
    this._edgesBySource = new Map()
    this._edgesByTarget = new Map()
    this.ids = new Set()
    // The type map maps type value to count of individuals of given type.
    // The type set is an array of type values (to fascilitate reactivity).
    this.typeSet = []
    this.typeMap = new Map()

    // Holds a map of component individual id value -> embracing individual IRI.
    // Ie the component is pointing to parent / embracing individual.
    this.aggregates = new Map()

    // Holds a map of individual id value -> geo coordinate
    this.geoCoordinates = new Map()

    // Selectors
    this.indexSelector = new IndexSelector({ ids: this.ids })
    this.geoCoordSelector = new GeoCoordSelector(geoCoordinateFn)
    this.typeSelector = new TypeSelector({ ids: this.ids })
    this.treeSelector = new TreeSelector(
      { ids: this.ids, edges: this.edges, cache: this },
      upTreeEdges,
      downTreeEdges
    )
    this.assetPathSelector = assetPathTypeCategories
      ? new AssetPathSelector(
          { cache: this, ids: this.ids },
          assetPathTypeCategories
        )
      : null

    // Queue for reads initiated while cache is is initial read all state.
    // Maps id value to object with a resolve and reject function.
    this.promisesQueue = new Map()

    // Persistent state messages from client operations (e.g. transaction failures/recoveries).
    // Each message: { id, severity ('error'|'warning'|'info'), title, detail, journalData, timestamp }
    this.persistentMessages = []

    // Mount event handlers.
    addEventListener(IndividualRefreshEvent, this.refresh.bind(this))
    addEventListener(IndividualDeleteEvent, this.delete.bind(this))
    addEventListener(CollectionSizeUpdateEvent, this.sizeUpdate.bind(this))
    addEventListener(SyncProgressEvent, this.syncProgressUpdate.bind(this))
    addEventListener(SyncSkippedIdsEvent, this.syncSkippedIds.bind(this))
    addEventListener(CacheSyncCompleteEvent, this.syncComplete.bind(this))
    addEventListener(
      PersistentStateMessageEvent,
      this._onPersistentMessage.bind(this)
    )

    // Synchronous initialization of the initial read sequence.
    this.client.init()

    // Initalize cache properties.
    this._size = 0

    // Sync flag.
    this.isSynced = false
    // Bumped on every individual added, updated or removed. Derived views
    // compare it with what they last built from: unchanged means the graph is
    // exactly the one they already rendered, so rebuilding would reproduce
    // their own output.
    this.revision = 0

    // Sync progress tracking (client-specific units: loaded/total)
    this.syncProgress = { loaded: 0, total: 0 }

    // Last sync timestamp (from IndexedDB metadata)
    this.lastSyncTime = null

    // IndexedDB for persistence
    // DEFERRED: IDB is NOT initialized here - we need user context first
    // Call initializeWithUser(userId) after authentication to enable caching
    this.idb = null
    this.idbPendingCount = 0 // Track new individuals since last IndexedDB save
    this.idbLastSaveTime = 0 // Timestamp of last save to prevent excessive saves
    this.idbSaveDebounceMs = 2000 // Wait 2 seconds after last change before saving

    // Flag to indicate if we've loaded from IndexedDB
    this.loadedFromIDB = false

    // Track which individuals are seen during re-sync to detect deletions
    this.resyncSeenIds = null // Set of IDs seen during re-sync, or null if not re-syncing

    // IDB initialization is deferred until user is known
    // This promise will be set by initializeWithUser() or remain null for anonymous users
    this.idbInitPromise = null
  }

  /**
   * Initialize IndexedDB with user-specific database
   * Must be called after authentication to enable caching
   * @param {string} userId - User identifier (e.g., MSAL homeAccountId)
   * @returns {Promise<void>}
   * @public
   */
  async initializeWithUser(userId) {
    if (!userId) {
      console.log(
        'RecCache: No userId provided, skipping IndexedDB initialization (anonymous mode)'
      )
      return
    }

    // Close existing connection if any (user switching)
    if (this.idb) {
      this.idb.close()
    }

    // Create user-specific IDB instance
    console.log(`RecCache: Initializing IndexedDB for user: ${userId}`)
    this.idb = new RecCacheIDB(userId)

    // Start IDB initialization and hydration
    this.idbInitPromise = this._initializeIndexedDB()

    return this.idbInitPromise
  }

  /**
   * Start loading/syncing data
   * This is the main entry point for the Vue app to load data
   * Internally waits for IDB initialization before fetching from API
   * @public
   */
  async startSync() {
    // Wait for IndexedDB initialization to complete (if enabled)
    // This ensures we check for cached data before fetching from API
    if (this.idbInitPromise) {
      await this.idbInitPromise
    }

    // Tell the client whether the cache has data (from IDB hydration).
    // If not, the client should ignore its persisted change-detection state
    // since there's nothing in memory for it to protect.
    const isCacheHydrated = this.individuals.length > 0

    // Reconcile the client's persisted change-detection state against what
    // hydration actually produced. Without this the client could skip a twin
    // that is not in memory, silently leaving it missing from the cache.
    if (isCacheHydrated) {
      this.client.retainChangeDetection(this._individualsById.keys())
    }

    this.client.readAllRequest({ isCacheHydrated })
  }

  get size() {
    return this._size
  }

  get inCache() {
    return this.individuals.length
  }

  // Clears the databases of the REC cache and selectors
  clear() {
    this.individuals = []
    this._individualsById.clear()
    this.edges = []
    this._edgesBySource.clear()
    this._edgesByTarget.clear()
    this.ids.clear()
    this.typeSet = []
    this.typeMap.clear()
    this.aggregates.clear()
    this.geoCoordinates.clear()
    this._size = 0
    this.isSynced = false
    this.lastSyncTime = null

    this.indexSelector.clear()
    this.geoCoordSelector.clear()
    this.typeSelector.clear()
    this.treeSelector.clear()
    if (this.assetPathSelector) this.assetPathSelector.clear()

    this.promisesQueue.clear()

    // Close IndexedDB connection (user logout/switch)
    // IDB data remains in browser storage and can be loaded again on next login
    if (this.idb) {
      this.idb.close()
      this.idb = null
      this.idbInitPromise = null
    }
  }

  /**
   * Trigger a background resync from the API
   * Keeps current data visible while syncing - updates/deletes happen as data arrives
   */
  resync() {
    console.log('RecCache: Starting background resync from API...')
    this.isSynced = false
    this.syncProgress = { loaded: 0, total: 0 }
    // Data is already in memory, so keep change-detection state to skip unchanged twins.
    this.client.readAllRequest({ isCacheHydrated: true })
  }

  /**
   * Initialize IndexedDB and load cached data in the background
   * This runs asynchronously without blocking RecCache construction
   * @private
   */
  async _initializeIndexedDB() {
    if (!this.idb) {
      console.log(
        'RecCache: IDB not initialized (no user context), skipping cache load'
      )
      return
    }

    try {
      console.log('RecCache: Initializing IndexedDB in background...')
      await this.idb.init()

      // Try to load cached data
      const metadata = await this.idb.loadMetadata()

      if (!metadata || !metadata.size) {
        console.log('RecCache: No cached data found, will load from API')
        return
      }

      console.log(
        `RecCache: Found cached data (${
          metadata.size
        } individuals, last updated: ${new Date(
          metadata.lastUpdate
        ).toLocaleString()})`
      )

      // Load individuals
      const serializedIndividuals = await this.idb.loadIndividuals()
      if (serializedIndividuals.length === 0) {
        console.log('RecCache: No individuals in cache')
        return
      }

      // Integrity check: verify stored count matches actual data
      // A mismatch means a previous save was interrupted (e.g. page reload mid-write)
      if (serializedIndividuals.length !== metadata.size) {
        console.warn(
          `RecCache: Integrity check failed - metadata says ${metadata.size} individuals but found ${serializedIndividuals.length}. Clearing corrupt cache...`
        )
        await this.idb.clear()
        return
      }

      // Give the client back the change-detection state saved with these
      // individuals — before anything below forgets entries for records that
      // fail to deserialize. Absent on the first load after it moved here from
      // localStorage; the client then keeps the legacy copy it read itself,
      // and startSync() prunes either one against what hydration produced.
      const changeDetection = await this.idb.loadChangeDetection()
      if (changeDetection) {
        this.client.restoreChangeDetection?.(changeDetection)
      }

      console.log(
        `RecCache: Hydrating cache with ${serializedIndividuals.length} individuals...`
      )

      // Deserialize and populate cache, with per-individual error handling
      // so one corrupt record doesn't break the entire hydration
      let deserializationErrors = 0
      const failedIds = [] // Track IDs of individuals that failed to deserialize
      serializedIndividuals.forEach((serialized) => {
        try {
          const individual = this._deserializeIndividual(serialized)

          // Basic sanity check: individual must have id and types
          if (
            !individual.id ||
            !individual.id.value ||
            !individual.types ||
            individual.types.length === 0
          ) {
            deserializationErrors++
            if (serialized?.id) failedIds.push(serialized.id)
            return
          }

          this.individuals.push(individual)
          this._individualsById.set(individual.id.value, individual)
          this.ids.add(individual.id.value)

          // Update type tracking
          individual.types.forEach((t) => {
            const typeValue = t.value
            if (!this.typeMap.has(typeValue)) {
              this.typeMap.set(typeValue, 0)
              this.typeSet.push(typeValue)
            }
            this.typeMap.set(typeValue, this.typeMap.get(typeValue) + 1)
          })

          // Rebuild edges
          individual.properties
            .filter((p) => p instanceof ObjectProperty)
            .forEach((p) =>
              p.values.forEach((v) =>
                this._addEdge(new Edge(individual.id, v, p.name))
              )
            )
        } catch (err) {
          deserializationErrors++
          if (serialized?.id) failedIds.push(serialized.id)
          console.warn(
            `RecCache: Failed to deserialize individual ${
              serialized?.id || 'unknown'
            }`,
            err
          )
        }
      })

      // If too many records failed to deserialize, the cache is likely corrupt
      if (deserializationErrors > 0) {
        const errorRate = deserializationErrors / serializedIndividuals.length
        console.warn(
          `RecCache: ${deserializationErrors} of ${serializedIndividuals.length} individuals failed to deserialize`
        )
        if (errorRate > 0.05) {
          // More than 5% corrupt - discard entire cache
          console.warn(
            'RecCache: Too many deserialization errors, clearing corrupt cache...'
          )
          this.individuals = []
          this._individualsById.clear()
          this.edges = []
          this._edgesBySource.clear()
          this._edgesByTarget.clear()
          this.ids.clear()
          this.typeSet = []
          this.typeMap.clear()
          await this.idb.clear()
          return
        }

        // Proactively forget change-detection state for failed IDs so they get
        // re-fetched during the upcoming sync instead of being skipped.
        if (failedIds.length > 0) {
          console.warn(
            `RecCache: Forgetting change-detection for ${failedIds.length} failed IDs to force re-fetch:`,
            failedIds
          )
          this.client.forgetChangeDetection(failedIds)
        }
      }

      // Reconstruct aggregates from edges (NOT loaded from IDB).
      // Aggregates must be derived from the same individuals/edges we just loaded
      // to avoid cross-store inconsistency from partial IDB writes.
      this.edges.forEach((edge) => {
        if (this.downAggregators.includes(edge.label.value)) {
          // Down aggregator: source is parent, target is child
          this.aggregates.set(edge.target.value, edge.source)
        }
        if (this.upAggregators.includes(edge.label.value)) {
          // Up aggregator: source is child, target is parent
          this.aggregates.set(edge.source.value, edge.target)
        }
      })
      console.log(
        `RecCache: Reconstructed ${this.aggregates.size} aggregates from edges`
      )

      // Reconstruct geoCoordinates from individuals (NOT loaded from IDB).
      // Same rationale: derive from source of truth to avoid stale data.
      if (this.geoCoordinatesFn) {
        this.individuals.forEach((individual) => {
          const geoCoord = this.geoCoordinatesFn(individual)
          if (geoCoord) {
            this.geoCoordinates.set(individual.id.value, geoCoord)
            individual.meta.geoCoords.addGeoCoord(geoCoord)
          }
        })
      }
      console.log(
        `RecCache: Reconstructed ${this.geoCoordinates.size} geoCoordinates from individuals`
      )

      // Update size and last sync time
      this._size = metadata.size
      // Prefer lastSyncTime (when API was checked), fall back to lastUpdate (when cache was written)
      this.lastSyncTime = metadata.lastSyncTime || metadata.lastUpdate || null

      // Don't mark as synced yet - we need to wait for API re-sync to complete
      // This ensures the UI shows loading state and sync completion fires properly
      this.isSynced = false
      this.loadedFromIDB = true

      // Initialize re-sync tracking to detect deletions during API read cycle
      // Any individual NOT seen during the upcoming read cycle was deleted
      this.resyncSeenIds = new Set()

      console.log(
        `RecCache: Successfully hydrated cache from IndexedDB (${this.individuals.length} individuals, ${this.edges.length} edges)`
      )
      console.log(
        'RecCache: Will track re-sync to detect deleted individuals...'
      )

      // Manually trigger selector updates without event storm
      // Call update() on each selector for every individual to rebuild their internal state
      console.log('RecCache: Rebuilding selectors from cached individuals...')

      this.individuals.forEach((individual) => {
        // IndexSelector.update() expects { detail: individual }
        this.indexSelector.update({ detail: individual })

        // GeoCoordSelector.update() expects { detail: individual }
        this.geoCoordSelector.update({ detail: individual })

        // TypeSelector.update() expects { detail: individual }
        this.typeSelector.update({ detail: individual })

        // TreeSelector.update() expects { detail: individual }
        this.treeSelector.update({ detail: individual })

        // AssetPathSelector.update() expects { detail: individual }
        if (this.assetPathSelector) {
          this.assetPathSelector.update({ detail: individual })
        }
      })

      console.log('RecCache: Selectors rebuilt successfully')
      console.log(
        '  - IndexSelector index size:',
        this.indexSelector.index.size
      )
      console.log(
        '  - GeoCoordSelector coords:',
        this.geoCoordSelector.geoCoords.size
      )
      console.log('  - TypeSelector types:', this.typeSelector.typeMap.size)
      console.log('  - TreeSelector roots:', this.treeSelector.roots.size)

      // Update tree roots so tree can be built from cached data
      this.treeSelector.updateRoots()
      console.log(
        '  - TreeSelector roots after updateRoots:',
        this.treeSelector.roots.size
      )

      // Dispatch hydration complete event to allow UI to render cached data
      // This enables the tree view to be available immediately from IndexedDB cache
      // while the API re-sync continues in the background
      dispatchEvent(new CustomEvent(CacheHydrationCompleteEvent))
      console.log('RecCache: Dispatched hydration complete event')

      // NOTE: Don't fire CacheSyncCompleteEvent here!
      // We need to wait for the API re-sync to complete so that resyncSeenIds is properly populated.
      // Otherwise cleanup will think all cached individuals are deleted (empty resyncSeenIds set).
      // The event will be fired naturally when the API sync completes via syncComplete().
    } catch (error) {
      console.error('RecCache: Error initializing IndexedDB', error)
      // On any initialization error, try to clear the corrupt database
      // so the next load starts fresh rather than hitting the same error
      try {
        if (this.idb) {
          console.warn('RecCache: Clearing potentially corrupt IndexedDB...')
          await this.idb.deleteDatabase()
          // Re-initialize with empty database for saving after API sync
          this.idb = new RecCacheIDB(this.idb.userId)
          await this.idb.init()
        }
      } catch (cleanupError) {
        console.error(
          'RecCache: Failed to clean up corrupt IndexedDB',
          cleanupError
        )
        this.idb = null
      }
    }
  }

  // TODO cache the Individuals array with lower case in constructor.
  isIndividual(id) {
    return !!Individuals.find((i) => i.toLowerCase() == id.value.toLowerCase())
  }

  // Returns Boolean.
  // Parameter id is an IRI.
  isComponent(id) {
    // console.log(
    //   'rec-cache.rec-cache.is-component',
    //   id,
    //   this.aggregates.has(id.value)
    // )
    return this.aggregates.has(id.value)
  }

  // Returns array of component individual ids as id values.
  // Parameter id is an IRI or id value.
  hasComponents(id) {
    // console.log(
    //   'rec-cache.rec-cache.has-components',
    //   id,
    //   [...this.aggregates.entries()]
    //     .filter((e) => e[1].value == (id.value || id))
    //     .map((e) => e[0])
    // )
    return [...this.aggregates.entries()]
      .filter((e) => e[1].value == (id.value || id))
      .map((e) => e[0])
  }

  // Returns parent individual id as IRI.
  // Parameter id is an IRI or id value.
  getComponentParent(id) {
    // console.log(
    //   'rec-cache.rec-cache.get-component-parent',
    //   id,
    //   this.aggregates.get(id.value || id)
    // )

    return this.aggregates.get(id.value || id)
  }

  // Returns array of GeoCoordinate. Or undefined if not found.
  // Parameter id is an Iri.
  readGeoCoords(id, aggregate = false) {
    if (!this.ids.has(id.value)) return undefined
    const targetId = aggregate
      ? this.isComponent(id)
        ? this.getComponentParent(id)
        : id
      : id
    // Identify geo coordinates on the individual itself and potentially its components.
    const components = aggregate ? [...this.hasComponents(targetId)] : []
    return components
      .concat([targetId.value])
      .map((id) => this.geoCoordinates.get(id))
      .filter((c) => !!c)
  }

  // Returns array of individual id values.
  hasGeoCoords(aggregate = false) {
    return [...this.geoCoordinates.keys()].map((i) =>
      aggregate ? (this.isComponent(i) ? this.getComponentParent(i) : i) : i
    )
  }

  // Returns RecIndividual. Or undefined if not found.
  // For component individuals their parent individual is returned.
  // Parameter id is an IRI or an id value.
  //
  // TODO consider an async version for future proofness.
  read(id, aggregate = false) {
    // TODO crude implementation, refactor logic, see over the other rec cache methods as well.
    id = id instanceof Iri ? id : new Iri(id)

    if (!this.ids.has(id.value)) return undefined

    const targetId = aggregate
      ? this.isComponent(id)
        ? this.getComponentParent(id)
        : id
      : id

    // By id, not `this.individuals.find(...)`: that array is reactive, so a
    // scan ran a Vue getter per individual — ~14ms per read at 46k, and the
    // detail panel reads once per related individual it shows.
    const individual = this._individualsById.get(targetId.value)

    // console.log(
    //   'rec-cache.rec-cache.read.target',
    //   JSON.parse(JSON.stringify(individual))
    // )

    // Inclusion of aggregated individuals as components.
    const components = this.hasComponents(targetId).map((cId) =>
      this._individualsById.get(cId)
    )
    // console.log('rec-cache.rec-cache.read.components', components)

    // TODO components emptied to avoid every read adds the same component.
    // Implies that aggregation wont work on individuals with components.
    //
    individual.components = []
    components.forEach((c) => {
      // Fix for not yet read component individuals. May be changed to a read to cache.
      // TODO or find a way to represent an empty component individual.
      if (c) {
        const component = new RecComponent()
        component.id = c.id
        component.types = c.types
        component.properties = c.properties.filter(
          (p) => p instanceof DataProperty
        )
        component.meta = { geoCoords: c.meta.geoCoords }
        individual.components.push(component)
      }
    })

    // if (individual.components.length)
    //   console.log('rec-cache.rec-cache.read.individual', individual.components)

    return individual
  }

  readAsync(id, aggregate = false) {
    if (this.isSynced) return this.read(id, aggregate)

    // Create and que promises, then start read cycle.
    id = id instanceof Iri ? id : new Iri(id)

    if (this.promisesQueue.has(id.value)) return

    const promiseFns = {}
    const promise = new Promise((resolve, reject) => {
      promiseFns.resolve = resolve
      promiseFns.reject = reject
    })

    this.promisesQueue.set(id.value, promiseFns)
    return promise
  }

  // Internal: add an edge and update indices.
  _addEdge(edge) {
    this.edges.push(edge)
    const sv = edge.source.value || edge.source
    const tv = edge.target.value || edge.target
    if (!this._edgesBySource.has(sv)) this._edgesBySource.set(sv, [])
    this._edgesBySource.get(sv).push(edge)
    if (!this._edgesByTarget.has(tv)) this._edgesByTarget.set(tv, [])
    this._edgesByTarget.get(tv).push(edge)
  }

  /**
   * Rebuild all edges from scratch by scanning every individual's ObjectProperties.
   * Self-healing: eliminates any edge inconsistency from incremental updates.
   * Analogous to TreeSelector.rebuildEdges().
   */
  rebuildEdges() {
    const prevCount = this.edges.length
    this.edges = []
    this._edgesBySource.clear()
    this._edgesByTarget.clear()

    this.individuals.forEach((individual) => {
      individual.properties
        .filter((p) => p instanceof ObjectProperty)
        .forEach((p) =>
          p.values.forEach((v) =>
            this._addEdge(new Edge(individual.id, v, p.name))
          )
        )
    })

    console.log(`RecCache: rebuildEdges() — ${prevCount}→${this.edges.length}`)
  }

  // Internal: remove all edges matching a predicate and rebuild affected index entries.
  // The general path: predicates can match by source, target, or anything else,
  // so we still need a full scan to find matches. Index updates afterwards
  // use the existing index entries (O(per-source/target)) instead of
  // re-filtering all edges.
  _removeEdges(predicate) {
    const removed = this.edges.filter(predicate)
    if (removed.length === 0) return
    const removedSet = new Set(removed)
    this.edges = this.edges.filter((e) => !removedSet.has(e))
    // Rebuild index entries for affected sources/targets — walk the existing
    // index entries (O(per-source/target)) rather than this.edges (O(all)).
    const affectedSources = new Set(
      removed.map((e) => e.source.value || e.source)
    )
    const affectedTargets = new Set(
      removed.map((e) => e.target.value || e.target)
    )
    for (const sv of affectedSources) {
      const existing = this._edgesBySource.get(sv) || []
      const remaining = existing.filter((e) => !removedSet.has(e))
      if (remaining.length) this._edgesBySource.set(sv, remaining)
      else this._edgesBySource.delete(sv)
    }
    for (const tv of affectedTargets) {
      const existing = this._edgesByTarget.get(tv) || []
      const remaining = existing.filter((e) => !removedSet.has(e))
      if (remaining.length) this._edgesByTarget.set(tv, remaining)
      else this._edgesByTarget.delete(tv)
    }
  }

  // Internal fast path: remove all edges with the given source value. Uses
  // _edgesBySource directly so the no-op case (source has no edges) is O(1)
  // rather than O(all edges). Hot path on `refresh()` where every cache
  // update calls this once — without the fast path, a 4k-individual sync
  // does 4k × all-edges scans for typically empty results.
  _removeEdgesBySource(sourceValue) {
    const removed = this._edgesBySource.get(sourceValue)
    if (!removed || removed.length === 0) return
    const removedSet = new Set(removed)
    this._edgesBySource.delete(sourceValue)
    this.edges = this.edges.filter((e) => !removedSet.has(e))
    const affectedTargets = new Set(
      removed.map((e) => e.target.value || e.target)
    )
    for (const tv of affectedTargets) {
      const existing = this._edgesByTarget.get(tv) || []
      const remaining = existing.filter((e) => !removedSet.has(e))
      if (remaining.length) this._edgesByTarget.set(tv, remaining)
      else this._edgesByTarget.delete(tv)
    }
  }

  // Returns array of Edge.
  // Parameters are string literals.
  queryEdges({ source = null, target = null, label = null } = {}) {
    let candidates
    if (source) {
      candidates = this._edgesBySource.get(source) || []
    } else if (target) {
      candidates = this._edgesByTarget.get(target) || []
    } else {
      candidates = this.edges
    }
    const result = candidates
      .filter((e) => !source || e.source.equals(source))
      .filter((e) => !target || e.target.equals(target))
      .filter((e) => !label || e.label.equals(label))
    return result
  }

  degree(id) {
    const iri = id instanceof Iri ? id : new Iri(id)
    const incoming = this.queryEdges({ target: iri.value })
    // TODO down aggregators only used on outgoing...
    const outgoing = this.queryEdges({ source: iri.value }).filter(
      (e) => !this.downAggregators.find((a) => e.label.value == a)
    )
    console.log('DEGREE', iri.value, incoming, outgoing)
    return incoming.length + outgoing.length
  }

  // Event handler for updating databases. Receives a CustomEvent argument.
  // Parameter jsonldIndividual is an object.
  // TODO the format of the JSON-LDs passed aroun d must be looked over, formally an expande such is an array...
  refresh({ detail: jsonldIndividual }) {
    // console.log('rec-cache.rec-cache.rec-cache.refresh', jsonldIndividual)

    // Parse the received object.
    const individual = this.owl.parse(jsonldIndividual)

    // Decide if new or update - check ids.
    const isUpdate = this.ids.has(individual.id.value)
    // TODO may be refactoed since the splice below will return an array of the deleted individual
    // TODO doesnt seem to be needed!?
    // const oldIndividual =
    //   isUpdate &&
    //   this.individuals.find((i) => i.id.value == individual.id.value)

    // Store to individuals. Overwrite existing if update.
    if (isUpdate) {
      // The individual is updated in place to support reactive mechanisms.
      // O(1) lookup via the id index — was Array.find, which dominated
      // sync wall-clock at 4k+ cache size (~12ms/individual on its own).
      const storedIndividual = this._individualsById.get(individual.id.value)

      // Capture old type BEFORE updating for type change tracking
      const oldTypeValue = storedIndividual.type.value

      // Update ALL fields, not just properties
      // In ADT, updates can change: properties, components, and theoretically types
      //
      // Preserve down-aggregator ObjectProperties (area, capacity, etc.) if they
      // are missing from the incoming individual. This happens when the update
      // originates from toJSONLD() which serializes components as inline blank
      // nodes that owl.parse() drops — the resulting individual has no
      // ObjectProperties for its components, which would break edges/aggregates.
      const incomingObjPropNames = new Set(
        individual.properties
          .filter((p) => p instanceof ObjectProperty)
          .map((p) => p.name.value)
      )
      const preservedAggProps = storedIndividual.properties.filter(
        (p) =>
          p instanceof ObjectProperty &&
          this.downAggregators.includes(p.name.value) &&
          !incomingObjPropNames.has(p.name.value)
      )
      storedIndividual.properties = [
        ...individual.properties,
        ...preservedAggProps,
      ]
      storedIndividual.types = individual.types
      storedIndividual.meta = individual.meta
      // Note: 'type' is a getter-only computed property derived from 'types' array,
      // so updating 'types' above automatically updates 'type' - no assignment needed

      // Note: Don't overwrite storedIndividual.components here because:
      // 1. Component individuals arrive as separate refresh() events and update themselves
      // 2. Components have deterministic IDs (parent.componentType) so they update in place
      // 3. Parent.components array is updated dynamically when each component individual arrives
      // 4. This preserves cached components until their refresh events arrive during sync

      // Update type tracking if types changed
      // Note: This is rare in ADT (model usually doesn't change), but technically possible
      const newTypeValue = individual.type.value
      if (oldTypeValue !== newTypeValue) {
        // Decrement old type count
        const oldCount = this.typeMap.get(oldTypeValue)
        if (oldCount === 1) {
          this.typeMap.delete(oldTypeValue)
        } else {
          this.typeMap.set(oldTypeValue, oldCount - 1)
        }
        // Increment new type count
        const newCount = this.typeMap.get(newTypeValue)
        this.typeMap.set(newTypeValue, newCount ? newCount + 1 : 1)
        this.typeSet = [...this.typeMap.keys()]
      }

      // TODO add to updated queue (that is a mechanism to acknowledge an update by the app).
    } else {
      // If initial sync already complete and new individual arrives (event/exception),
      // increment expected size
      if (this.isSynced && this.individuals.length == this._size) {
        this._size++
      }
      this.individuals.push(individual)
      this._individualsById.set(individual.id.value, individual)
      // TODO crude implementation with both set (for reactivity) and map, to keep track, refactor...
      const typeCount = this.typeMap.get(individual.type.value)
      this.typeMap.set(individual.type.value, typeCount ? typeCount + 1 : 1)
      this.typeSet = [...this.typeMap.keys()]
    }

    // TODO consider updating edges in place to support reactivity... needed?
    // Update edges - remove exisitng edges if update, then create edges.
    if (isUpdate) {
      // Source-only fast path: O(1) when the individual has no outgoing
      // edges (the typical case for ref:* and other isolated nodes), and
      // O(per-source-edges) when it does. The generic _removeEdges scans
      // all edges and was the dominant cost on big syncs.
      this._removeEdgesBySource(individual.id.value)
    }
    // Create edge for each object property value.
    individual.properties
      .filter((p) => p instanceof ObjectProperty)
      .forEach((p) =>
        p.values.forEach((v) =>
          this._addEdge(new Edge(individual.id, v, p.name))
        )
      )

    // Update aggregates. The aggregates map id value of child to IRI of parent.
    // Identify down aggregates, ie the source of the edge is the parent.
    // O(k) over the source's edges via the index — was O(edges) per
    // individual, which scaled badly with cache size.
    const sourceEdges = this._edgesBySource.get(individual.id.value) || []
    const downAggEdges = sourceEdges.filter((e) =>
      this.downAggregators.includes(e.label.value)
    )
    downAggEdges.forEach((e) =>
      this.aggregates.set(e.target.value, individual.id)
    )
    // Identify up aggregates, ie the target of the edge is the parent.
    sourceEdges
      .filter((e) => this.upAggregators.includes(e.label.value))
      .forEach((e) => this.aggregates.set(e.source.value, e.target))

    // TODO add update support, determine if a geo coord has been added or changed (two cases)

    // Add geo coordinates.
    // TODO add support for reactivity, ie let application provide reactive variable.
    // TODO add supprot for several geo coords per individual, ie the geoCoordinatesFn must return array.
    if (this.geoCoordinatesFn) {
      const geoCoord = this.geoCoordinatesFn(individual)
      if (isUpdate) {
        this.geoCoordinates.delete(individual.id.value)
        // TODO when support for multiple this should be a filter on the list. Se also above.
        individual.meta.geoCoords.list = []
      }
      if (geoCoord) this.geoCoordinates.set(individual.id.value, geoCoord)
      if (geoCoord) individual.meta.geoCoords.addGeoCoord(geoCoord)
    }

    // Update parent.components array if this individual is a component
    // This makes the UI responsive as components arrive during sync
    // Components are identified by being in the aggregates map (they have a parent)
    if (this.aggregates.has(individual.id.value)) {
      const parentId = this.aggregates.get(individual.id.value)
      const parent = this._individualsById.get(parentId.value)

      if (parent) {
        // Convert RecIndividual to RecComponent (simpler structure for components)
        const component = new RecComponent(this.namespaces)
        component.id = individual.id
        component.types = individual.types
        // Components only include data properties (not object properties)
        component.properties = individual.properties.filter(
          (p) => p instanceof DataProperty
        )
        component.meta = { geoCoords: individual.meta.geoCoords }

        // Update or add to parent's components array
        const existingIndex = parent.components.findIndex(
          (c) => c.id.value === individual.id.value
        )
        if (existingIndex >= 0) {
          // Update existing component in place for Vue reactivity
          parent.components.splice(existingIndex, 1, component)
        } else {
          // Add new component
          parent.components.push(component)
        }
      }
    }

    // Retroactively attach existing component individuals to this parent.
    // When a parent (e.g. Building) is processed, its down-aggregator edges
    // register children (e.g. area, capacity) in the aggregates map. If those
    // children already exist in the cache (e.g. during updates or when dispatch
    // order places parent after components), attach them to parent.components.
    const targetIndividual = isUpdate
      ? this._individualsById.get(individual.id.value)
      : individual
    if (downAggEdges.length > 0 && targetIndividual) {
      downAggEdges.forEach((e) => {
        const componentInd = this._individualsById.get(e.target.value)
        if (componentInd) {
          const component = new RecComponent(this.namespaces)
          component.id = componentInd.id
          component.types = componentInd.types
          component.properties = componentInd.properties.filter(
            (p) => p instanceof DataProperty
          )
          component.meta = { geoCoords: componentInd.meta.geoCoords }

          const existingIndex = targetIndividual.components.findIndex(
            (c) => c.id.value === componentInd.id.value
          )
          if (existingIndex >= 0) {
            targetIndividual.components.splice(existingIndex, 1, component)
          } else {
            targetIndividual.components.push(component)
          }
        }
      })
    }

    // Add to ids.
    this.ids.add(individual.id.value)

    // Track this ID during re-sync to detect deletions
    if (this.resyncSeenIds !== null) {
      this.resyncSeenIds.add(individual.id.value)
    }

    // Issue events for selections.
    // TODO consider rename to cache refresh event...
    this.revision++
    dispatchEvent(new CustomEvent(CacheUpdateEvent, { detail: individual }))

    // Save to IndexedDB
    // Strategy: Debounced saves to avoid overwhelming IndexedDB
    // - During initial sync: only save once when complete (handled in wasSynced check above)
    // - During re-sync after cache load: debounce to avoid 16k saves
    // - For real-time updates: debounce to batch rapid changes
    if (!isUpdate) {
      this.idbPendingCount++
      // Save is triggered when sync completes (see !wasSynced && this.isSynced check above)
    } else {
      // For updates, use debounced save to batch rapid changes
      if (this.isSynced) {
        this.idbPendingCount++
        this._debouncedSaveToCache()
      }
    }
  }

  // Parameter id is an identity string.
  delete({ detail: id }) {
    console.log('rec-cache.rec-cache.rec-cache.delete', id)

    const type = this.read(id).type

    // Remove from cache databases and update size.
    this.individuals = this.individuals.filter((i) => i.id.value != id)
    this._individualsById.delete(id)

    // Remove properties from in-neighbor...
    // TODO would be beneficial if the individual wasnt persisted but rather created at read, from properties (triples)
    const incoming = (this._edgesByTarget.get(id) || []).slice()
    incoming.forEach((edge) => {
      const neighbor = this._individualsById.get(edge.source.value)
      neighbor?.getProperty(edge.label)?.removeValue(id)
    })

    // Both incoming and outgoing edges are removed.
    this._removeEdges((e) => e.source.value == id || e.target.value == id)
    this.aggregates.delete(id)
    const parents = []
    // TODO the removal of aggregates needs to be looked over for ADT
    // distinguishing between components and embeddings of leaves for the UI...
    this.aggregates.forEach((value, key) => {
      if (value.value == id) {
        parents.push(key)
      }
    })
    parents.forEach((p) => this.aggregates.delete(p))
    this.ids.delete(id)
    this._size--
    // Remove from type set if its the last individual of this type, ie count equals one.
    const typeCount = this.typeMap.get(type.value)
    if (typeCount == 1) {
      this.typeMap.delete(type.value)
      this.typeSet = [...this.typeMap.keys()]
    } else {
      this.typeMap.set(type.value, typeCount - 1)
    }
    this.geoCoordinates.delete(id)

    // Update selectors by issuing cache delete event.
    this.revision++
    dispatchEvent(new CustomEvent(CacheDeleteEvent, { detail: id }))

    // Save to IndexedDB to persist the deletion
    if (this.isSynced) {
      this.idbPendingCount++
      this._debouncedSaveToCache()
    }
  }

  // Eventhandler for the size update event.
  // NOTE: This may no longer be needed with queue-based sync completion,
  // but kept for backwards compatibility with clients that dispatch it.
  sizeUpdate({ detail: size }) {
    console.log(
      `RecCache: Size update received: ${size} (current individuals: ${this.individuals.length})`
    )
    this._size = size
  }

  // Event handler for sync progress updates from client
  syncProgressUpdate({ detail: progress }) {
    this.syncProgress = progress
    console.log(
      `RecCache: Sync progress - ${progress.loaded} of ${progress.total} (client units)`
    )
  }

  // Event handler for SyncSkippedIdsEvent from the client.
  // The client's change-detection filter skips unchanged twins to avoid expensive
  // parsing, but those twins still exist in ADT. We add their IDs to resyncSeenIds
  // so the sync-complete cleanup doesn't mistake them for deleted twins.
  //
  // SAFETY: Validates that each skipped twin is actually in the cache. If a twin
  // was skipped by change detection but is NOT in the cache (e.g., IDB hydration
  // failed for it, or IDB was partially corrupt), we tell the client to forget its
  // change-detection state so the twin is re-fetched on the next sync cycle. Without
  // this check, phantom entries cause twins to be permanently missing from the cache.
  syncSkippedIds(event) {
    const skippedIds = event.detail
    if (this.resyncSeenIds !== null && skippedIds) {
      let componentCount = 0
      const phantomIds = []
      skippedIds.forEach((id) => {
        // Verify the skipped twin is actually in the cache
        if (!this.ids.has(id)) {
          phantomIds.push(id)
          return // Don't mark as seen — it's not in the cache
        }
        this.resyncSeenIds.add(id)
        // Also mark components of this twin as seen.
        // Components have deterministic IDs (e.g., "building-01.area") and are
        // stored as separate individuals in the cache. If the parent twin is
        // skipped by change detection, its components won't arrive via refresh()
        // and would otherwise be incorrectly deleted by _cleanupDeletedIndividuals().
        const componentIds = this.hasComponents({ value: id })
        componentIds.forEach((cId) => {
          this.resyncSeenIds.add(cId)
          componentCount++
        })
      })

      if (phantomIds.length > 0) {
        console.warn(
          `RecCache: ${phantomIds.length} twins were skipped by change detection but are NOT in cache — forgetting their change-detection state so they are re-fetched next sync:`,
          phantomIds
        )
        this.client.forgetChangeDetection(phantomIds)
      }

      console.log(
        `RecCache: Marking ${
          skippedIds.length - phantomIds.length
        } change-detection-skipped twins and ${componentCount} components as still present` +
          (phantomIds.length > 0
            ? ` (${phantomIds.length} phantom entries cleared)`
            : '')
      )
    }
  }

  // Event handler for sync completion from client (when queue empties)
  syncComplete() {
    console.log('RecCache: Sync complete event received from client')
    this.isSynced = true
    this.lastSyncTime = Date.now()

    // Always update lastSyncTime in IDB (even if no data changes)
    if (this.idb) {
      this.idb.updateLastSyncTime(this.lastSyncTime)
    }

    // Clean up deleted individuals after re-sync
    if (this.resyncSeenIds !== null) {
      this._cleanupDeletedIndividuals()
    }

    // Save to IndexedDB now that initial sync is complete
    if (this.idbPendingCount > 0) {
      console.log(
        `RecCache: Sync complete, saving ${this.individuals.length} individuals to IndexedDB...`
      )
      this._debouncedSaveToCache()
    }

    // Reset resync tracking for next cycle
    this.resyncSeenIds = null
  }

  /**
   * Clean up individuals that were deleted (not seen during re-sync)
   * Called after re-sync completes to remove stale cached data
   * @private
   */
  _cleanupDeletedIndividuals() {
    if (this.resyncSeenIds === null) {
      console.warn('RecCache: Cannot clean up - no re-sync tracking active')
      return
    }

    // Find individuals in cache that were NOT seen during re-sync
    const deletedIds = []
    this.individuals.forEach((individual) => {
      if (!this.resyncSeenIds.has(individual.id.value)) {
        deletedIds.push(individual.id.value)
      }
    })

    if (deletedIds.length > 0) {
      console.log(
        `RecCache: Detected ${deletedIds.length} deleted individuals during re-sync, removing...`
      )

      // Remove each deleted individual (reuse delete logic without event dispatch)
      deletedIds.forEach((id) => {
        // Remove from individuals array
        this.individuals = this.individuals.filter((i) => i.id.value !== id)
        this._individualsById.delete(id)

        // Remove edges
        this._removeEdges((e) => e.source.value === id || e.target.value === id)

        // Remove from aggregates
        this.aggregates.delete(id)
        const parents = []
        this.aggregates.forEach((value, key) => {
          if (value.value === id) {
            parents.push(key)
          }
        })
        parents.forEach((p) => this.aggregates.delete(p))

        // Remove from other data structures
        this.ids.delete(id)
        this.geoCoordinates.delete(id)

        // Dispatch delete event for selectors
        this.revision++
        dispatchEvent(new CustomEvent(CacheDeleteEvent, { detail: id }))
      })

      // Update size and type tracking
      this._size = this.individuals.length

      // Rebuild type map and set
      this.typeMap.clear()
      this.typeSet = []
      this.individuals.forEach((individual) => {
        const typeValue = individual.type.value
        const count = this.typeMap.get(typeValue) || 0
        this.typeMap.set(typeValue, count + 1)
      })
      this.typeSet = [...this.typeMap.keys()]

      // Also remove deleted components from parent.components arrays
      // This ensures parents don't reference components that no longer exist
      this.individuals.forEach((parent) => {
        if (parent.components && parent.components.length > 0) {
          parent.components = parent.components.filter(
            (comp) => !deletedIds.includes(comp.id.value)
          )
        }
      })

      console.log(
        `RecCache: Cleanup complete, removed ${deletedIds.length} deleted individuals`
      )
      console.log(
        `RecCache: Cache now has ${this.individuals.length} individuals`
      )

      // Mark for save
      this.idbPendingCount += deletedIds.length

      // After cleanup, verify sync is still true
      this.isSynced = this.individuals.length == this._size
    } else {
      console.log('RecCache: Re-sync complete, no deletions detected')
    }

    // Clear re-sync tracking
    this.resyncSeenIds = null
  }

  /**
   * Debounced save to IndexedDB
   * Waits for a quiet period before actually saving to batch multiple rapid changes
   * @private
   */
  _debouncedSaveToCache() {
    // Clear any existing pending save
    if (this.idbSaveTimeout) {
      clearTimeout(this.idbSaveTimeout)
    }

    // Schedule a new save after the debounce delay
    this.idbSaveTimeout = setTimeout(() => {
      this.idbSaveTimeout = null
      // Don't save during initial sync — the cache contains a mix of stale IDB
      // data and fresh API data, which can produce inconsistent state on reload.
      // Wait until syncComplete() fires, which triggers its own save.
      if (this.idbPendingCount > 0 && this.isSynced) {
        this.saveToCache('debounce').catch((error) => {
          console.error('RecCache: Debounced save to IndexedDB failed', error)
        })
      }
    }, this.idbSaveDebounceMs)
  }

  /**
   * Save current cache state to IndexedDB
   * @returns {Promise<void>}
   */
  async saveToCache(caller = 'unknown') {
    if (!this.idb || !this.idb.isReady) {
      console.warn(
        `IDB-SAVE [${caller}]: BLOCKED — idb=${!!this.idb}, isReady=${
          this.idb?.isReady
        }`
      )
      return
    }

    // Guard against concurrent saves (e.g. debounce + syncComplete racing)
    if (this._idbSaveInProgress) {
      console.warn(`IDB-SAVE [${caller}]: BLOCKED — save already in progress`)
      return
    }

    this._idbSaveInProgress = true

    try {
      const individualCount = this.individuals.length
      console.log(
        `IDB-SAVE [${caller}]: Saving ${individualCount} individuals...`
      )

      // Only individuals + metadata are persisted. Derived data (edges,
      // aggregates, geoCoordinates) is reconstructed from individuals on load.
      await this.idb.saveAll({
        individuals: this.individuals,
        metadata: {
          size: individualCount,
          lastUpdate: Date.now(),
          isSynced: this.isSynced,
        },
        // Persisted beside the individuals in the same transaction; opaque to
        // the cache. See RecCacheIDB.saveAll().
        changeDetection: this.client.getChangeDetectionState?.(),
      })

      // Only now can the client safely drop any older copy it keeps elsewhere.
      this.client.changeDetectionPersisted?.()

      this.idbPendingCount = 0
      this.idbLastSaveTime = Date.now()
      this.lastSyncTime = Date.now()

      // Read back from IDB to verify the write actually persisted
      const lastModifiedId =
        this.individuals.length > 0
          ? this.individuals[this.individuals.length - 1].id.value
          : null
      const verification = await this.idb.verifyWrite(
        individualCount,
        lastModifiedId
      )

      if (!verification.ok) {
        console.error(
          `IDB-SAVE [${caller}]: VERIFICATION FAILED!`,
          verification
        )
      } else {
        console.log(
          `IDB-SAVE [${caller}]: Verified OK — ${verification.count} individuals in IDB`
        )
      }
    } catch (error) {
      console.error(`IDB-SAVE [${caller}]: ERROR`, error)
    } finally {
      this._idbSaveInProgress = false
    }
  }

  /**
   * Deserialize a plain object back into a RecIndividual
   * @private
   */
  _deserializeIndividual(serialized) {
    // Create a proper RecIndividual instance with methods
    const individual = new RecIndividual(this.namespaces)

    // Reconstruct id and type
    individual.id = this._deserializeIri(serialized.idObject)
    // Filter out any null types that failed to deserialize
    individual.types = serialized.types
      .map((t) => this._deserializeIri(t))
      .filter((t) => t !== null)

    // Reconstruct properties (filter out nulls in case of corrupted data)
    individual.properties = serialized.properties
      .map((p) => this._deserializeProperty(p))
      .filter((p) => p !== null)

    // Reconstruct components (filter out nulls)
    individual.components = serialized.components
      .map((c) => this._deserializeComponent(c))
      .filter((c) => c !== null)

    // Reconstruct meta with GeoCoords
    const geoCoords = new GeoCoords()
    if (serialized.meta?.geoCoords?.list) {
      serialized.meta.geoCoords.list.forEach((coord) => {
        geoCoords.addGeoCoord(new GeoCoord(coord.latitude, coord.longitude))
      })
    }
    individual.meta = { geoCoords }

    return individual
  }

  /**
   * Deserialize an IRI
   * @private
   */
  _deserializeIri(serialized) {
    if (!serialized) return null
    return new Iri(serialized.value, this.namespaces)
  }

  /**
   * Deserialize a Property
   * @private
   */
  _deserializeProperty(serialized) {
    const name = this._deserializeIri(serialized.name)
    const values = serialized.values.map((v) => this._deserializeValue(v))

    if (serialized._type === 'DataProperty') {
      return new DataProperty(name, ...values)
    } else if (serialized._type === 'ObjectProperty') {
      return new ObjectProperty(name, ...values)
    }

    return null
  }

  /**
   * Deserialize a Value
   * @private
   */
  _deserializeValue(serialized) {
    if (!serialized) return null

    if (serialized._type === 'Iri') {
      return this._deserializeIri(serialized)
    } else if (serialized._type === 'LanguageString') {
      return new LanguageString(serialized.value, serialized.language)
    } else if (serialized._type === 'JsonLiteral') {
      return new JsonLiteral(serialized.value)
    } else if (serialized._type === 'Literal') {
      // datatype is stored as a plain string (IRI value), not a serialized Iri
      return new Literal(serialized.value, serialized.datatype || undefined)
    }

    return null
  }

  /**
   * Deserialize a Component
   * @private
   */
  _deserializeComponent(serialized) {
    const component = new RecComponent()
    component.id = this._deserializeIri(serialized.id)
    component.types = serialized.types.map((t) => this._deserializeIri(t))
    component.properties = serialized.properties.map((p) =>
      this._deserializeProperty(p)
    )

    // Reconstruct meta with GeoCoords if present
    const geoCoords = new GeoCoords()
    if (serialized.meta?.geoCoords?.list) {
      serialized.meta.geoCoords.list.forEach((coord) => {
        geoCoords.addGeoCoord(new GeoCoord(coord.latitude, coord.longitude))
      })
    }
    component.meta = { geoCoords }

    return component
  }

  // Manipulations

  // Parameter type is an ontology Iri
  async createAsync(type) {
    console.log('rec-cache.rec-cache.rec-cache.create-async', type)
    try {
      const id = await this.client.createRequest(type)
      console.log('rec-cache.rec-cache.rec-cache.create-async.id', id)

      // Safety net: forget change-detection for the new twin so the next sync
      // always re-reads it from ADT, ensuring IDB gets the authoritative data.
      this.client.forgetChangeDetection(id)

      // Persist to IDB immediately so the new twin survives a page reload.
      if (this.idbPendingCount > 0 && this.isSynced) {
        if (this.idbSaveTimeout) {
          clearTimeout(this.idbSaveTimeout)
          this.idbSaveTimeout = null
        }
        await this.saveToCache('createAsync')
      }

      return Promise.resolve(new Iri(id))
    } catch (error) {
      console.log('rec-cache.rec-cache.rec-cache.create-async.error', error)

      // TODO add some generalized error handling, will be needed for all manipulation methods...
      return Promise.reject()
    }
  }

  // Delete an individual and all its components.
  // TODO introduce guard rails to avoid destryonig integrity of graph...
  // TODO currently the individual view performs checks... should do so in future too but library should check (perhaps based on parameter)
  // Parameter id is an Iri.
  async deleteAsync(id) {
    console.log('rec-cache.rec-cache.rec-cache.delete-async', id)
    try {
      // Get the individual to find its components before deletion
      const individual = this.read(id)
      const componentIds = individual?.components?.map((c) => c.id) || []

      // Delete the individual from ADT - ADT automatically deletes components when parent twin is deleted
      await this.client.deleteRequest(id)

      // Dispatch delete events for components so they are removed from local cache
      // The main individual delete event is dispatched by client.deleteRequest
      componentIds.forEach((componentId) => {
        dispatchEvent(
          new CustomEvent(IndividualDeleteEvent, { detail: componentId.value })
        )
      })

      // Persist deletion to IDB so deleted twins don't reappear on reload
      if (this.idbSaveTimeout) {
        clearTimeout(this.idbSaveTimeout)
        this.idbSaveTimeout = null
      }
      if (this.idbPendingCount > 0 && this.isSynced) {
        await this.saveToCache('deleteAsync')
      }
    } catch (error) {
      console.log('rec-cache.rec-cache.rec-cache.delete-async.error', error)

      // TODO add some generalized error handling, will be needed for all manipulation methods...
      return Promise.reject()
    }
  }

  // Refresh a single individual from persistent storage.
  // Reads the latest twin and relationships from ADT, updates the in-memory cache
  // through the existing IndividualRefreshEvent flow, and returns the fresh individual.
  // Parameter id is an Iri or id value string.
  async refreshIndividualAsync(id) {
    const idValue = id instanceof Iri ? id.value : id
    console.log('rec-cache.rec-cache.refresh-individual-async', idValue)
    await this.client.readIndividualRequest(idValue)
    // By this point the synchronous IndividualRefreshEvent dispatch has already
    // updated the in-memory cache via the refresh() handler.
    return this.read(id, true)
  }

  // Parameter individual is an Individual.
  async updateAsync(individual) {
    console.log('rec-cache.rec-cache.rec-cache.update-async', individual)
    try {
      // Call the update method on the client with JSON-LD.
      // updateRequest dispatches IndividualRefreshEvent synchronously, which
      // updates the in-memory cache (properties, edges, aggregates) via refresh().
      // refresh() also schedules a debounced IDB save — we cancel that below and
      // do our own awaited save so the data is durably persisted before returning.
      await this.client.updateRequest(individual.toJSONLD())

      // Safety net: forget change-detection for the updated twin so the next sync
      // always re-reads it from ADT. This guarantees that even if the IDB save
      // below fails or stores stale data, the sync cycle will correct it.
      this.client.forgetChangeDetection(individual.id.value)

      // Persist to IDB immediately so the updated properties survive a page reload.
      // Cancel the debounced save that refresh() scheduled — we do our own awaited flush.
      if (this.idbSaveTimeout) {
        clearTimeout(this.idbSaveTimeout)
        this.idbSaveTimeout = null
      }
      if (this.idbPendingCount > 0 && this.isSynced) {
        await this.saveToCache('updateAsync')
      }

      return Promise.resolve()
    } catch (error) {
      console.log('rec-cache.rec-cache.rec-cache.update-async.error', error)

      // TODO add some generalized error handling, will be needed for all manipulation methods...
      return Promise.reject()
    }
  }

  // Change the type of an individual.
  // Parameters: id is an Iri, newType is an Iri.
  // The client handles the full delete + recreate transaction with journal.
  async changeTypeAsync(id, newType) {
    console.log('rec-cache.rec-cache.change-type-async', id, newType)
    try {
      // Get component IDs before the operation (they will be deleted with the twin)
      const individual = this.read(id)
      const componentIds = individual?.components?.map((c) => c.id) || []

      await this.client.changeTypeRequest(id, newType)

      // Dispatch delete events for components so they are removed from local cache.
      // The main twin delete+refresh events are dispatched by the client during the transaction.
      componentIds.forEach((componentId) => {
        dispatchEvent(
          new CustomEvent(IndividualDeleteEvent, {
            detail: componentId.value,
          })
        )
      })

      // Persist type change to IDB
      if (this.idbSaveTimeout) {
        clearTimeout(this.idbSaveTimeout)
        this.idbSaveTimeout = null
      }
      if (this.idbPendingCount > 0 && this.isSynced) {
        await this.saveToCache('changeTypeAsync')
      }

      return Promise.resolve()
    } catch (error) {
      console.log('rec-cache.rec-cache.change-type-async.error', error)

      // Surface a persistent error message if the transaction failed
      if (error.journal) {
        this._addPersistentMessage({
          severity: 'error',
          title: 'Type change failed',
          detail: error.message,
          journalData: error.journal,
        })
      }

      return Promise.reject(error)
    }
  }

  // Recover from an incomplete change-type transaction found at startup.
  // Called after authentication and sync initialization.
  async recoverPendingTransactions() {
    try {
      const result = await this.client.recoverChangeTypeTransaction()
      if (result) {
        console.log('rec-cache.rec-cache.recover-pending-transactions', result)
        const severity = result.recovered ? 'info' : 'error'
        this._addPersistentMessage({
          severity,
          title: result.recovered
            ? 'Transaction recovered'
            : 'Transaction recovery failed',
          detail: result.message,
          journalData: result.journal,
        })
      }
    } catch (error) {
      console.error(
        'rec-cache.rec-cache.recover-pending-transactions.error',
        error
      )
    }
  }

  // Persistent state message system.
  // Messages are surfaced to the UI for user awareness of background state issues.

  _onPersistentMessage(event) {
    this._addPersistentMessage(event.detail)
  }

  _addPersistentMessage({ severity, title, detail, journalData }) {
    const message = {
      id: Date.now() + '-' + Math.random().toString(36).substring(2, 7),
      severity,
      title,
      detail,
      journalData: journalData || null,
      timestamp: new Date().toISOString(),
    }
    this.persistentMessages.push(message)
    console.log(
      `rec-cache.persistent-message [${severity}]:`,
      title,
      detail,
      journalData
    )
  }

  dismissPersistentMessage(messageId) {
    const index = this.persistentMessages.findIndex((m) => m.id === messageId)
    if (index !== -1) {
      this.persistentMessages.splice(index, 1)
    }
  }

  gremlin() {
    return new Traversal(this.individuals, this.edges)
  }
}

export { RecCache }
